// External Imports
import { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import Modal from "../UI/Modal";

/** ========================================================================
 * * MANAGE SUBSCRIPTION
 * Renders a page, which displays details of a specific customer's subscription.
 * ========================================================================== */

export default function AddSubscription({ customer, open, setOpen, refresh }) {
  // Initialize States
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ======================= INPUT CHANGE HANDLER ======================= */
  const onPlanSelection = (plan) => {
    const price = plans.find((p) => p.id === plan).price;
    document.getElementById("amount").value = price;
    document.getElementById(
      "description"
    ).value = `${plan.toUpperCase()} Subscription Plan set up fee.`;
  };

  /* ======================= FETCH SUBSCRIPTION PLANS ======================= */
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch-all", {
        table: "plan",
      });
      setPlans(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= ADD/EDIT SUBSCRIPTION ======================= */
  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        customerId: customer.id,
        planId: document.getElementById("plan").value,
        trial: document.getElementById("trial").value === "Yes",
        amount: document.getElementById("amount").value,
        daysUntilDue: document.getElementById("daysUntilDue").value,
        description: document.getElementById("description").value,
      };
      await requestAdminAPI("manage-subscription", payload);
      refresh() && refresh();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= CANCEL SUBSCRIPTION ======================= */
  const cancelPlanHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        customerId: customer.id,
        cancel: true,
      };
      await requestAdminAPI("manage-subscription", payload);
      setOpen(false);
      refresh() && refresh();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Customer and Plans on Load
  useEffect(() => {
    if (open) fetchPlans();
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[600px]">
        <Heading
          title="Manage Subscription"
          text="Edit the subscription plan for this customer."
          isBackArrow={false}
        >
          <MdClose onClick={() => setOpen(false)} className="text-2xl icon" />
        </Heading>

        <form onSubmit={submitHandler} className="grid gap-8">
          <div className="grid text-sm lg:grid-cols-2">
            <DetailPair
              label="Customer"
              value={`${customer.firstName} ${customer.lastName}`}
              loading={loading}
            />
            <DetailPair
              label="Current Plan"
              value={customer.plan?.plan?.name}
              loading={loading}
            />
          </div>

          <div className="grid gap-4 text-sm">
            <p className="text-xs text-stone-400">
              Customers can be charged for any changes to subscription plan. The
              amount is sent as an invoice. For no charges, simply leave the
              amount empty.
            </p>
            <div className="grid gap-4 lg:grid-cols-2">
              <SelectPair
                label="New Plan"
                options={plans.map((plan) => ({
                  id: plan.id,
                  name: plan.name,
                }))}
                inputOptions={{
                  onChange: (e) => onPlanSelection(e.target.value),
                  id: "plan",
                }}
                edit={true}
                loading={loading}
              />
              <SelectPair
                label="2 Week Trial"
                options={["Yes", "No"]}
                inputOptions={{
                  id: "trial",
                  name: "trial",
                }}
                edit={true}
                loading={loading}
              />
              <DetailPair
                label="Invoice Amount"
                edit={true}
                inputOptions={{
                  type: "number",
                  step: "0.01",
                  id: "amount",
                  defaultValue: plans[0]?.price,
                }}
                loading={loading}
              />
              <DetailPair
                label="Days Until Due"
                edit={true}
                inputOptions={{
                  type: "number",
                  id: "daysUntilDue",
                  defaultValue: "1",
                }}
                loading={loading}
              />
            </div>
            <DetailPair
              label="Invoice Description"
              edit={true}
              inputOptions={{
                type: "text",
                id: "description",
                required: true,
                defaultValue: `${plans[0]?.name.toUpperCase()} Subscription Plan set up fee.`,
              }}
              loading={loading}
            />
            <div className="grid gap-4 lg:grid-cols-2">
              {customer?.plan && (
                <div onClick={cancelPlanHandler} className="btn-warning">
                  Cancel Plan
                </div>
              )}
              <button className="btn-primary">
                {customer.plan ? "Change Plan" : "Add Plan"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
